import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, ApolloError } from "@apollo/client";
import { print } from "graphql";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,

} from "@/components/ui/card";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Upload, Loader2, CheckCircle, XCircle, Info, ImageIcon, Search, Building, Image } from "lucide-react";
import { GET_TRAINING_BY_ID_ADMIN } from "@/lib/graphql/queries";
import { getCookie } from "@/utils/cookies";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { fetchFilteredProviders, setProviderFilter } from "@/features/provider/providerSlice";
import ProviderLogo from "@/components/common/ProviderLogo";
import { UPDATE_TRAINING } from "@/lib/graphql/mutations/trainingMutaions";
import { env } from '@/utils/env';
import { GET_TRAINING_IMAGE } from "@/lib/graphql/queries";

// TrainingImage Component
const TrainingImage = ({ filename, alt, className }: { filename: string; alt: string; className: string }) => {
    console.log("TrainingImage component received filename:", filename);

    // Skip the query if filename is empty or starts with http
    const skipQuery = !filename || filename.startsWith("http");
    console.log("Skip query?", skipQuery, "Reason:", !filename ? "empty filename" : filename.startsWith("http") ? "starts with http" : "will query");

    const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
        variables: { filename },
        skip: skipQuery,
        onCompleted: (data) => {
            console.log("Image data received:", data);
        },
        onError: (error) => {
            console.error("Error fetching image:", error);
        }
    });

    if (loading) {
        console.log("Image loading...");
        return (
            <div className={`${className} flex items-center justify-center bg-gray-100 animate-pulse`}>
                <ImageIcon className="text-gray-400" aria-hidden="true" />
            </div>
        );
    }

    if (error || !data) {
        console.error("Error loading image:", error?.message || "No data");
        return (
            <div className={`${className} flex items-center justify-center bg-gray-100`}>
                <ImageIcon className="text-gray-400" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs p-1 text-center">
                    Error loading image
                </div>
            </div>
        );
    }

    // If filename starts with http, use it directly
    if (filename.startsWith("http")) {
        console.log("Using direct URL for image:", filename);
        return (
            <img
                src={filename}
                alt={alt}
                className={`${className} object-cover`}
                loading="lazy"
            />
        );
    }

    // Otherwise use the base64 data from the query
    if (data.getTrainingImageByName?.base64Data) {
        const src = `data:${data.getTrainingImageByName?.mimetype};base64,${data.getTrainingImageByName?.base64Data}`;
        console.log("Using base64 data for image, mimetype:", data.getTrainingImageByName?.mimetype);
        return (
            <img
                src={src}
                alt={alt}
                className={`${className} object-cover`}
                loading="lazy"
            />
        );
    }

    // Fallback
    console.log("No image data available, showing fallback");
    return (
        <div className={`${className} flex items-center justify-center bg-gray-100`}>
            <ImageIcon className="text-gray-400" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 right-0 bg-yellow-500 text-white text-xs p-1 text-center">
                No image data
            </div>
        </div>
    );
};

// Type definitions
interface Instructor {
    name: string;
    email: string;
    specialization: string;
}

interface Module {
    title: string;
    description: string;
}

interface Lab {
    title: string;
    description: string;
}

interface Resource {
    name: string;
    description: string;
}

interface TrainingInput {
    title: string;
    provider: string;
    providerId?: number; // Add providerId field
    price: number;
    duration: string;
    description: string;
    level: string;
    format: string;
    category: string;
    startDate: string;
    language: string;
    certificate: boolean;
    instructor: Instructor;
    requirements: string[];
    modules: Module[];
    labs: Lab[];
    resources: Resource[];
    location?: string;
    image?: string; // Add image field
}

// Updated GraphQL mutation for updating a training


const EditTraining = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState("basic");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const token = getCookie('token');
    const dispatch = useDispatch();
    const { providers, loading: providersLoading } = useSelector((state: RootState) => state.provider);

    // Add provider search state
    const [providerSearchTerm, setProviderSearchTerm] = useState("");
    const [showProviderSearch, setShowProviderSearch] = useState(false);
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);

    // Form state
    const [trainingData, setTrainingData] = useState<Partial<TrainingInput>>({
        title: "",
        provider: "",
        price: 0,
        duration: "",
        description: "",
        level: "Beginner",
        format: "Online",
        category: "Programming",
        startDate: "",
        language: "English",
        certificate: false,
        instructor: { name: "", email: "", specialization: "" },
        requirements: [],
        modules: [],
        labs: [],
        resources: [],
        location: "",
    });

    // Image handling
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>("");

    // Current item states for arrays
    const [currentRequirement, setCurrentRequirement] = useState("");
    const [currentModule, setCurrentModule] = useState({ title: "", description: "" });
    const [currentLab, setCurrentLab] = useState({ title: "", description: "" });
    const [currentResource, setCurrentResource] = useState({ name: "", description: "" });

    // Fetch training data
    const { loading: fetchLoading, error: fetchError, data } = useQuery(GET_TRAINING_BY_ID_ADMIN, {
        fetchPolicy: 'network-only', // Force a network request to get fresh data
        variables: { id: parseInt(id || "0", 10) },
        skip: !id,
        onCompleted: (data) => {
            console.log("Raw training data from API:", data);
            if (data?.getTrainingById) {
                const training = data.getTrainingById;
                const startDate = training.startDate
                    ? new Date(training.startDate).toISOString().split('T')[0]
                    : "";
                setTrainingData({
                    title: training.title || "",
                    provider: training.provider || "",
                    providerId: training.providerId ? Number(training.providerId) : null,
                    price: training.price || 0,
                    duration: training.duration || "",
                    description: training.description || "",
                    level: training.level || "Beginner",
                    format: training.format || "Online",
                    category: training.category || "Programming",
                    startDate,
                    language: training.language || "English",
                    certificate: training.certificate || false,
                    instructor: training.instructor || { name: "", email: "", specialization: "" },
                    requirements: training.requirements || [],
                    modules: training.modules || [],
                    labs: training.labs || [],
                    resources: training.resources || [],
                    location: training.location || "",
                    image: training.image || "", // Make sure to include the image field
                });

                // Log the image field for debugging
                console.log("Training image from API:", training.image);
                if (training.providerId) {
                    setSelectedProviderId(Number(training.providerId));
                }
            }
        }
    });

    // Update training mutation
    const [updateTraining] = useMutation(UPDATE_TRAINING);

    // Handle image change
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    // Clear selected image
    const clearSelectedImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    // Array item handlers
    const addRequirement = () => {
        if (currentRequirement.trim()) {
            setTrainingData(prev => ({
                ...prev,
                requirements: [...(prev.requirements || []), currentRequirement.trim()]
            }));
            setCurrentRequirement("");
        }
    };

    const removeRequirement = (index: number) => {
        setTrainingData(prev => ({
            ...prev,
            requirements: (prev.requirements || []).filter((_, i) => i !== index),
        }));
    };

    const addModule = () => {
        if (currentModule.title.trim() && currentModule.description.trim()) {
            setTrainingData(prev => ({
                ...prev,
                modules: [...(prev.modules || []), currentModule]
            }));
            setCurrentModule({ title: "", description: "" });
        }
    };

    const removeModule = (index: number) => {
        setTrainingData(prev => ({
            ...prev,
            modules: (prev.modules || []).filter((_, i) => i !== index),
        }));
    };

    const addLab = () => {
        if (currentLab.title.trim() && currentLab.description.trim()) {
            setTrainingData(prev => ({
                ...prev,
                labs: [...(prev.labs || []), currentLab]
            }));
            setCurrentLab({ title: "", description: "" });
        }
    };

    const removeLab = (index: number) => {
        setTrainingData(prev => ({
            ...prev,
            labs: (prev.labs || []).filter((_, i) => i !== index),
        }));
    };

    const addResource = () => {
        if (currentResource.name.trim() && currentResource.description.trim()) {
            setTrainingData(prev => ({
                ...prev,
                resources: [...(prev.resources || []), currentResource]
            }));
            setCurrentResource({ name: "", description: "" });
        }
    };

    const removeResource = (index: number) => {
        setTrainingData(prev => ({
            ...prev,
            resources: (prev.resources || []).filter((_, i) => i !== index),
        }));
    };


    useEffect(() => {
        if (showProviderSearch) {
            dispatch(setProviderFilter({ search: providerSearchTerm }));
            dispatch(fetchFilteredProviders({
                search: providerSearchTerm,
                page: 1,
                limit: 10
            }) as any);
        }
    }, [providerSearchTerm, showProviderSearch, dispatch]);

    // Add effect to set initial provider ID from training data
    useEffect(() => {
        if (data?.getTrainingById?.providerId) {
            setSelectedProviderId(data.getTrainingById.providerId);
        }
    }, [data]);

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setLoading(true);
            setError("");

            // Clean up the data by removing __typename from all objects
            const cleanModules = trainingData.modules?.map(({ title, description }) => ({
                title,
                description
            })) || [];

            const cleanLabs = trainingData.labs?.map(({ title, description }) => ({
                title,
                description
            })) || [];

            const cleanResources = trainingData.resources?.map(({ name, description }) => ({
                name,
                description
            })) || [];

            // Prepare input for the updated mutation structure
            const input = {
                id: parseInt(id || "0", 10),
                title: trainingData.title,
                description: trainingData.description,
                price: Number(trainingData.price),
                duration: trainingData.duration,
                level: trainingData.level,
                format: trainingData.format,
                category: trainingData.category,
                startDate: new Date(trainingData.startDate || "").toISOString(),
                language: trainingData.language,
                certificate: Boolean(trainingData.certificate),
                location: trainingData.format !== "Online" ? trainingData.location : null,
                instructor: {
                    name: trainingData.instructor?.name || "",
                    email: trainingData.instructor?.email || "",
                    specialization: trainingData.instructor?.specialization || "",
                },
                provider: trainingData.provider,
                providerId: trainingData.providerId ? Number(trainingData.providerId) : (selectedProviderId ? Number(selectedProviderId) : null),
                requirements: trainingData.requirements || [],
                modules: cleanModules,
                labs: cleanLabs,
                resources: cleanResources,
                image: trainingData.image, // Include the image field
            };

            // If there's a new image, use multipart form data
            if (imageFile) {
                // Create a new FormData instance
                const formData = new FormData();

                // Prepare the operations object with both input and image variables
                const operations = {
                    query: print(UPDATE_TRAINING),
                    variables: {
                        input: input,
                        image: null
                    }
                };

                // Define the map for file variables
                const map = { "0": ["variables.image"] };

                // Append the operations and map to the form data
                formData.append("operations", JSON.stringify(operations));
                formData.append("map", JSON.stringify(map));
                formData.append("0", imageFile);

                // Manual fetch to handle file upload with authentication token
                const response = await fetch(env.graphqlUrl, {
                    method: "POST",
                    body: formData,
                    headers: {
                        "x-apollo-operation-name": "UpdateTraining",
                        "Authorization": `Bearer ${token}` // Add the token here
                    },
                });

                const result = await response.json();
                console.log("Result:", result);

                if (result.errors) throw result.errors;

                toast({
                    title: "Success",
                    description: "Training updated successfully with new image",
                });

                //  navigate(`/admin/trainings/${id}`);
            } else {
                // Use regular mutation if no new image
                await updateTraining({
                    variables: { input },
                    context: {
                        headers: {
                            "Authorization": `Bearer ${token}` // Add the token here as well
                        }
                    }
                });

                toast({
                    title: "Success",
                    description: "Training updated successfully",
                });

                //igate(`/admin/trainings/${id}`);
            }
        } catch (err) {
            console.error("Error updating training:", err);
            setError(
                err instanceof ApolloError
                    ? err.message
                    : "Failed to update training. Please try again."
            );

            toast({
                title: "Error",
                description: err instanceof ApolloError ? err.message : "Failed to update training",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    if (fetchLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Loading training data...</span>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <XCircle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-xl font-bold mb-2">Error Loading Training</h2>
                <p className="text-gray-600">{fetchError.message}</p>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/admin/trainings')}
                >
                    Back to Trainings
                </Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Edit Training</h1>
                <Button
                    variant="outline"
                    onClick={() => navigate(`/admin/trainings/${id}`)}
                >
                    Cancel
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid grid-cols-5 mb-6">
                        <TabsTrigger value="basic">Basic Info</TabsTrigger>
                        <TabsTrigger value="instructor">Instructor</TabsTrigger>
                        <TabsTrigger value="content">Content</TabsTrigger>
                        <TabsTrigger value="resources">Resources</TabsTrigger>
                        <TabsTrigger value="media" className="relative">
                            Media
                            {trainingData.image && !imagePreview && (
                                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    {/* Basic Information Tab */}
                    <TabsContent value="basic">
                        <Card>
                            <CardHeader>
                                <CardTitle>Basic Information</CardTitle>
                                <CardDescription>Update the core details of this training</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="title">Course Title *</Label>
                                        <Input
                                            id="title"
                                            value={trainingData.title || ""}
                                            onChange={(e) => setTrainingData(prev => ({ ...prev, title: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    {/* Provider field with search functionality */}
                                    <div className="space-y-2">
                                        <Label htmlFor="provider">Provider *</Label>
                                        <div className="relative">
                                            <Input
                                                id="provider"
                                                value={trainingData.provider || ""}
                                                onChange={(e) => setTrainingData(prev => ({ ...prev, provider: e.target.value }))}
                                                required
                                                readOnly={showProviderSearch}
                                                onClick={() => setShowProviderSearch(true)}
                                                className="pr-10"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute right-0 top-0 h-full"
                                                onClick={() => setShowProviderSearch(!showProviderSearch)}
                                            >
                                                <Search className="h-4 w-4" />
                                            </Button>
                                        </div>

                                        {showProviderSearch && (
                                            <Card className="mt-2 absolute z-50 w-full max-w-md max-h-80 overflow-auto shadow-lg">
                                                <CardHeader className="py-2">
                                                    <div className="relative">
                                                        <Input
                                                            placeholder="Search providers..."
                                                            value={providerSearchTerm}
                                                            onChange={(e) => setProviderSearchTerm(e.target.value)}
                                                            className="pr-8"
                                                            autoFocus
                                                        />
                                                        <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="p-0">
                                                    {providersLoading ? (
                                                        <div className="flex justify-center items-center p-4">
                                                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                                        </div>
                                                    ) : providers.length > 0 ? (
                                                        // ... existing code ...

                                                        <div className="divide-y">
                                                            {providers.map((provider) => (

                                                                <div
                                                                    key={provider.id}
                                                                    className="p-3 hover:bg-muted cursor-pointer flex items-center gap-3"
                                                                    onClick={() => {
                                                                        const providerId = typeof provider.id === 'string' ? parseInt(provider.id, 10) : provider.id;
                                                                        setTrainingData(prev => ({
                                                                            ...prev,
                                                                            provider: provider.name,
                                                                            providerId: providerId
                                                                        }));
                                                                        setSelectedProviderId(providerId);
                                                                        setShowProviderSearch(false);
                                                                    }}
                                                                >
                                                                    <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                                                                        {provider.logoUrl ? (
                                                                            <ProviderLogo
                                                                                filename={provider.logoUrl}
                                                                                alt={provider.name}
                                                                            />
                                                                        ) : (
                                                                            <Building className="h-6 w-6 text-muted-foreground" />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="font-medium truncate">{provider.name}</p>
                                                                        <p className="text-sm text-muted-foreground truncate">
                                                                            {provider.type}
                                                                        </p>
                                                                    </div>
                                                                    {selectedProviderId === provider.id && (
                                                                        <CheckCircle className="h-5 w-5 text-primary" />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        // ... existing code ...
                                                    ) : (
                                                        <p className="p-4 text-center text-muted-foreground">No providers found</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="price">Price ($) *</Label>
                                        <Input
                                            id="price"
                                            type="number"
                                            value={trainingData.price || ""}
                                            onChange={(e) => setTrainingData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="duration">Duration *</Label>
                                        <Input
                                            id="duration"
                                            value={trainingData.duration || ""}
                                            onChange={(e) => setTrainingData(prev => ({ ...prev, duration: e.target.value }))}
                                            placeholder="e.g., 4 weeks"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="startDate">Start Date *</Label>
                                        <Input
                                            id="startDate"
                                            type="date"
                                            value={trainingData.startDate || ""}
                                            onChange={(e) => setTrainingData(prev => ({ ...prev, startDate: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="language">Language *</Label>
                                        <Select
                                            value={trainingData.language || "English"}
                                            onValueChange={(value) => setTrainingData(prev => ({ ...prev, language: value }))}
                                        >
                                            <SelectTrigger id="language">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="English">English</SelectItem>
                                                <SelectItem value="French">French</SelectItem>
                                                <SelectItem value="Arabic">Arabic</SelectItem>
                                                <SelectItem value="Spanish">Spanish</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Description *</Label>
                                    <Textarea
                                        id="description"
                                        value={trainingData.description || ""}
                                        onChange={(e) => setTrainingData(prev => ({ ...prev, description: e.target.value }))}
                                        className="min-h-32"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="level">Level</Label>
                                        <Select
                                            value={trainingData.level || "Beginner"}
                                            onValueChange={(value) => setTrainingData(prev => ({ ...prev, level: value }))}
                                        >
                                            <SelectTrigger id="level">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white z-50">                                                <SelectItem value="Beginner">Beginner</SelectItem>
                                                <SelectItem value="Intermediate">Intermediate</SelectItem>
                                                <SelectItem value="Advanced">Advanced</SelectItem>
                                                <SelectItem value="Expert">Expert</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="format">Format</Label>
                                        <Select
                                            value={trainingData.format || "Online"}
                                            onValueChange={(value) => setTrainingData(prev => ({ ...prev, format: value }))}
                                        >
                                            <SelectTrigger id="format">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white z-50">
                                                <SelectItem value="Online">Online</SelectItem>
                                                <SelectItem value="In-Person">In-Person</SelectItem>
                                                <SelectItem value="Hybrid">Hybrid</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="category">Category</Label>
                                        <Select
                                            value={trainingData.category || "Programming"}
                                            onValueChange={(value) => setTrainingData(prev => ({ ...prev, category: value }))}
                                        >
                                            <SelectTrigger id="category">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white z-50">
                                                <SelectItem value="Programming">Programming</SelectItem>
                                                <SelectItem value="Design">Design</SelectItem>
                                                <SelectItem value="Marketing">Marketing</SelectItem>
                                                <SelectItem value="Business">Business</SelectItem>
                                                <SelectItem value="Data Science">Data Science</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Conditional Location Field */}
                                {(trainingData.format === "In-Person" || trainingData.format === "Hybrid") && (
                                    <div className="space-y-2">
                                        <Label htmlFor="location">Location *</Label>
                                        <Input
                                            id="location"
                                            value={trainingData.location || ""}
                                            onChange={(e) => setTrainingData(prev => ({ ...prev, location: e.target.value }))}
                                            placeholder="Enter physical location"
                                            required
                                        />
                                    </div>
                                )}

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="certificate"
                                        checked={trainingData.certificate || false}
                                        onChange={(e) => setTrainingData(prev => ({ ...prev, certificate: e.target.checked }))}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <Label htmlFor="certificate">Certificate Upon Completion</Label>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Instructor Tab */}
                    <TabsContent value="instructor">
                        <Card>
                            <CardHeader>
                                <CardTitle>Instructor Information</CardTitle>
                                <CardDescription>Update the instructor details</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="instructorName">Instructor Name *</Label>
                                        <Input
                                            id="instructorName"
                                            value={trainingData.instructor?.name || ""}
                                            onChange={(e) => setTrainingData(prev => ({
                                                ...prev,
                                                instructor: { ...prev.instructor!, name: e.target.value }
                                            }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="instructorEmail">Instructor Email *</Label>
                                        <Input
                                            id="instructorEmail"
                                            type="email"
                                            value={trainingData.instructor?.email || ""}
                                            onChange={(e) => setTrainingData(prev => ({
                                                ...prev,
                                                instructor: { ...prev.instructor!, email: e.target.value }
                                            }))}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="instructorSpecialization">Specialization *</Label>
                                    <Input
                                        id="instructorSpecialization"
                                        value={trainingData.instructor?.specialization || ""}
                                        onChange={(e) => setTrainingData(prev => ({
                                            ...prev,
                                            instructor: { ...prev.instructor!, specialization: e.target.value }
                                        }))}
                                        required
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Content Tab */}
                    <TabsContent value="content">
                        <Card>
                            <CardHeader>
                                <CardTitle>Course Content</CardTitle>
                                <CardDescription>Update modules and requirements</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Requirements Section */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium">Requirements</h3>
                                    <div className="flex gap-2">
                                        <Input
                                            value={currentRequirement}
                                            onChange={(e) => setCurrentRequirement(e.target.value)}
                                            placeholder="Add a requirement"
                                        />
                                        <Button type="button" onClick={addRequirement}>Add</Button>
                                    </div>
                                    <div className="space-y-2">
                                        {trainingData.requirements?.map((req, index) => (
                                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                                <span>{req}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeRequirement(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Modules Section */}
                                <div className="space-y-4 pt-6 border-t">
                                    <h3 className="text-lg font-medium">Modules</h3>
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                value={currentModule.title}
                                                onChange={(e) => setCurrentModule({ ...currentModule, title: e.target.value })}
                                                placeholder="Module title"
                                            />
                                            <Input
                                                value={currentModule.description}
                                                onChange={(e) => setCurrentModule({ ...currentModule, description: e.target.value })}
                                                placeholder="Module description"
                                            />
                                        </div>
                                        <Button type="button" onClick={addModule} className="w-full">Add Module</Button>
                                    </div>
                                    <div className="space-y-3 mt-4">
                                        {trainingData.modules?.map((module, index) => (
                                            <div key={index} className="p-4 bg-gray-50 rounded-md">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-medium">{module.title}</h4>
                                                        <p className="text-sm text-gray-600">{module.description}</p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeModule(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Labs Section */}
                                <div className="space-y-4 pt-6 border-t">
                                    <h3 className="text-lg font-medium">Labs</h3>
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                value={currentLab.title}
                                                onChange={(e) => setCurrentLab({ ...currentLab, title: e.target.value })}
                                                placeholder="Lab title"
                                            />
                                            <Input
                                                value={currentLab.description}
                                                onChange={(e) => setCurrentLab({ ...currentLab, description: e.target.value })}
                                                placeholder="Lab description"
                                            />
                                        </div>
                                        <Button type="button" onClick={addLab} className="w-full">Add Lab</Button>
                                    </div>
                                    <div className="space-y-3 mt-4">
                                        {trainingData.labs?.map((lab, index) => (
                                            <div key={index} className="p-4 bg-gray-50 rounded-md">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-medium">{lab.title}</h4>
                                                        <p className="text-sm text-gray-600">{lab.description}</p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeLab(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Resources Tab */}
                    <TabsContent value="resources">
                        <Card>
                            <CardHeader>
                                <CardTitle>Additional Resources</CardTitle>
                                <CardDescription>Update supplementary materials</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium">Resources</h3>
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                value={currentResource.name}
                                                onChange={(e) => setCurrentResource({ ...currentResource, name: e.target.value })}
                                                placeholder="Resource name"
                                            />
                                            <Input
                                                value={currentResource.description}
                                                onChange={(e) => setCurrentResource({ ...currentResource, description: e.target.value })}
                                                placeholder="Resource description"
                                            />
                                        </div>
                                        <Button type="button" onClick={addResource} className="w-full">Add Resource</Button>
                                    </div>
                                    <div className="space-y-3 mt-4">
                                        {trainingData.resources?.map((resource, index) => (
                                            <div key={index} className="p-4 bg-gray-50 rounded-md">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-medium">{resource.name}</h4>
                                                        <p className="text-sm text-gray-600">{resource.description}</p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeResource(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Media Tab */}
                    <TabsContent value="media">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Image className="h-5 w-5" />
                                    Course Image
                                    {trainingData.image && (
                                        <Badge variant="outline" className="ml-2 text-xs font-normal">
                                            Image Exists
                                        </Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    {trainingData.image
                                        ? "Your training already has an image. You can keep it or upload a new one."
                                        : "Upload an image for your training to make it more attractive to learners."}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex-1 space-y-4">
                                        <Label htmlFor="image">Course Image</Label>
                                        <label htmlFor="image-upload" className="block">
                                            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer">
                                                <div className="flex flex-col items-center justify-center">
                                                    {imagePreview ? (
                                                        <>
                                                            <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                                                            <p className="text-sm font-medium">New image selected</p>
                                                            <p className="text-xs text-gray-500 mt-1">Click to change selection</p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                                            <p className="text-sm font-medium">Click or drag to upload a new image</p>
                                                            <p className="text-xs text-gray-500 mt-1">SVG, PNG, JPG or GIF (max. 2MB)</p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <Input
                                                type="file"
                                                id="image-upload"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                        </label>
                                        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-md text-sm text-blue-700">
                                            <Info className="h-4 w-4 flex-shrink-0" />
                                            <div>
                                                {trainingData.image ? (
                                                    <span>The current image will be kept if no new image is uploaded.</span>
                                                ) : (
                                                    <span>No image is currently set for this training.</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md:w-1/3">
                                        <div className="rounded-md overflow-hidden border w-full h-48 mb-2">
                                            {imagePreview ? (
                                                <img
                                                    src={imagePreview}
                                                    alt="Course preview"
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : trainingData.image ? (
                                                <div className="relative h-full w-full">
                                                    <TrainingImage
                                                        filename={trainingData.image}
                                                        alt={trainingData.title || "Course image"}
                                                        className="h-full w-full object-cover"
                                                    />
                                                    <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                                                        Current Image
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center bg-gray-100">
                                                    <ImageIcon className="h-12 w-12 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-center space-y-2">
                                            <p className="text-sm text-gray-500 text-center">
                                                {imagePreview ? "New Image Preview" : trainingData.image ? "Current Image" : "No Image"}
                                            </p>
                                            {imagePreview && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={clearSelectedImage}
                                                    className="text-xs flex items-center gap-1"
                                                >
                                                    <XCircle className="h-3 w-3" />
                                                    Remove Selection
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="mt-8 flex justify-end gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/admin/trainings/${id}`)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="min-w-32"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default EditTraining;
